import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Auth via cookie httpOnly impostato dal backend: il browser invia il cookie automaticamente
// (withCredentials). Nessun token in localStorage/sessionStorage (mitiga XSS).
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 503 && err?.response?.data?.detail === "maintenance") {
      // Manutenzione attiva: notifica l'app per mostrare subito la pagina dedicata.
      window.dispatchEvent(new Event("tb-maintenance-refresh"));
    }
    if (err?.response?.status === 401) {
      // La verifica sessione (/auth/me) NON deve redirigere: le pagine pubbliche
      // (home "in allestimento", /anteprima, /login) restano accessibili da anonimi.
      const url = err?.config?.url || "";
      const isSessionProbe = url.includes("/auth/me");
      if (!isSessionProbe && !window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// Download a payslip PDF (needs auth header -> fetch as blob)
export const downloadPayslip = async (id, filename) => {
  const res = await api.get(`/payslips/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "cedolino.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the employment contract summary sheet (PDF, auth header -> blob)
export const downloadContractSheet = async (empId, filename) => {
  const res = await api.get(`/employees/${empId}/contract-pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "scheda_contratto.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Export the employee list as CSV (auth header -> blob)
export const downloadEmployeesCsv = async (company = "all", external = "all", filename) => {
  const res = await api.get(`/employees/export?company=${company}&external=${external}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "elenco_dipendenti.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the seniority / scatti summary sheet (PDF, auth header -> blob)
export const downloadSenioritySheet = async (empId, filename) => {
  const res = await api.get(`/employees/${empId}/seniority-pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "anzianita.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the handover form (verbale di consegna) for a company asset (car|pc|phone)
export const downloadHandover = async (benefitId, asset, filename) => {
  const res = await api.get(`/benefits/${benefitId}/handover-pdf?asset=${asset}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `verbale_consegna_${asset}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download an accrual run export (csv | pdf) with auth header -> blob
export const downloadAccrualExport = async (runId, format, filename) => {
  const type = format === "pdf" ? "application/pdf" : "text/csv";
  const res = await api.get(`/accruals/${runId}/export?format=${format}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `maturazione.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the preavviso/prova report PDF (auth cookie -> blob)
export const downloadContractTermsReport = async (company = "all", filename) => {
  const res = await api.get(`/reports/contract-terms/pdf?company=${company}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "report_preavviso_prova.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download an asset registry report PDF (kind = car | device) with auth header -> blob
export const downloadAssetReport = async (kind, filename, includeReturned = true) => {
  const res = await api.get(`/benefits/report-pdf?kind=${kind}&include_returned=${includeReturned}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `report_parco_${kind}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the ESG car-km report PDF (year) with auth header -> blob
export const downloadEsgReport = async (year, filename) => {
  const res = await api.get(`/esg/report-pdf?year=${year}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `esg_km_auto_${year}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the ESG Data Readiness report PDF (year) with auth cookie -> blob
export const downloadEsgReadinessPdf = async (year, filename) => {
  const res = await api.get(`/esg/readiness/pdf?year=${year}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `esg_readiness_${year}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the operator guide PDF (audience = all | dipendente | hr) with auth header -> blob
export const downloadGuide = async (audience = "all", filename) => {
  const res = await api.get(`/guide/pdf?audience=${audience}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "guida_portale.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the access-log report PDF (filters) with auth header -> blob
export const downloadAccessLogPdf = async (params = "", filename) => {
  const res = await api.get(`/access-logs/export-pdf${params}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "registro_accessi.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Download the block-history report PDF (filters) with auth header -> blob
export const downloadBlockHistoryPdf = async (params = "", filename) => {
  const res = await api.get(`/security/block-history/export-pdf${params}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "storico_blocchi.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Upload a file (certificate / receipt) -> returns {id, filename, content_type, size}
export const uploadFile = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post("/files/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// Open an uploaded file in a new tab (auth header -> blob)
export const openFile = async (id) => {
  const res = await api.get(`/files/${id}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export default api;
